import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import "../../scss/backoffice.scss";
import "../../scss/backend/super-admin-config.scss";
import { Sidemenu } from "./Sidemenu";
import { Topbar } from "./Topbar";
import { Footer } from "./Footers";
import {
  GetPermission,
  AddPermissionGroup,
  UpdatePermissionGroup,
  DeletePermissionGroup,
} from "../../../src/services/ApiMenuPermissionGroup";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";

interface MenuPermissionGroup {
  id: number;
  groupObjectId: string;
  groupName: string;
  description: string;
  active: boolean;
  menuPermissions: Array<{
    id: number;
    menuId: number;
    permissionId: number;
    menuPermissionGroup: null;
  } | null>;
}

interface PermissionGroup {
  id: number;
  menuId: number;
  permissionId: number;
  menuPermissionGroup: MenuPermissionGroup;
}

const PermissionGroupMenu: React.FC = () => {
  const [hasError, setHasError] = useState(false);
  const [displayFullScreenLoading, setDisplayFullScreenLoading] =
    useState(false);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>(
    []
  );
  const [filteredGroups, setFilteredGroups] = useState<PermissionGroup[]>([]);
  const [editingGroup, setEditingGroup] = useState<MenuPermissionGroup | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchPermissionGroups();
  }, []);

  useEffect(() => {
    const filtered = permissionGroups.filter(
      (group) =>
        group.menuPermissionGroup.groupObjectId
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        group.menuPermissionGroup.groupName
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
    );
    setFilteredGroups(filtered);
  }, [searchTerm, permissionGroups]);

  const fetchPermissionGroups = async () => {
    setDisplayFullScreenLoading(true);
    try {
      const data = await GetPermission();
      console.log('data=',data)
      setPermissionGroups(data);
      setFilteredGroups(data);
    } catch (error) {
      setHasError(true);
      toast.error('Failed to fetch permission groups');
    } finally {
      setDisplayFullScreenLoading(false);
    }
  };


  const handleAddGroup = async (group: MenuPermissionGroup) => {
    try {
      await AddPermissionGroup(group);
      fetchPermissionGroups();
    } catch (error) {
      toast.error('Failed to add permission group');
    }
  };

  const handleUpdateGroup = async (group: MenuPermissionGroup) => {
    try {
      await UpdatePermissionGroup(group);
      fetchPermissionGroups();
    } catch (error) {
      toast.error('Failed to update permission group');
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    try {
      await DeletePermissionGroup(groupId);
      fetchPermissionGroups();
    } catch (error) {
      toast.error('Failed to delete permission group');
    }
  };

  const handleEditClick = (group: MenuPermissionGroup) => {
    setEditingGroup(group);
    setShowModal(true);
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (editingGroup) {
      if (editingGroup.id) {
        handleUpdateGroup(editingGroup);
      } else {
        handleAddGroup(editingGroup);
      }
      setEditingGroup(null);
      setShowModal(false);
    }
  };

  const handleDeleteClick = (groupId: number) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this permission group?");
    if (confirmDelete) {
      handleDeleteGroup(groupId);
      toast.success("Your permission group has been deleted.");
    }
  };

  return (
    <>
      <div className="container-fluid master-layout permissionGroup">
        <Sidemenu
          hasError={hasError}
          displayFullScreenLoading={displayFullScreenLoading}
          subTitle="Setting"
        />
        <div className="main-container">
          <div className="col-12 mb-4">
            <Topbar />
          </div>
          <div className="container-fluid">
            <Row className="align-items-center mb-3">
              <Col>
                <h1 className="title">Permission Group Management</h1>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by ObjectID or Group Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col className="text-end mb-4">
                <Button
                  variant="primary"
                  onClick={() => setShowModal(true)}
                  className="create-btn"
                >
                  Add Group
                </Button>
              </Col>
            </Row>
            {displayFullScreenLoading && <div>Loading...</div>}
            {!displayFullScreenLoading && hasError && (
              <div>Error loading permission groups</div>
            )}
            {!displayFullScreenLoading && !hasError && (
              <>
                <table className="table table-striped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>Group ObjectID</th>
                      <th>Group Name</th>
                      <th>Description</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGroups.map((group) => (
                      <tr key={group.menuPermissionGroup.groupObjectId}>
                        <td>{group.menuPermissionGroup.groupObjectId}</td>
                        <td>{group.menuPermissionGroup.groupName}</td>
                        <td>{group.menuPermissionGroup.description}</td>
                        <td>
                          {group.menuPermissionGroup.active
                            ? "Active"
                            : "Inactive"}
                        </td>
                        <td>
                          <Button
                            variant="warning"
                            className="edit-btn m-2 mt-0 mb-0"
                            onClick={() =>
                              handleEditClick(group.menuPermissionGroup)
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            className="delete-btn m-2 mt-0 mb-0"
                            variant="danger"
                            onClick={() =>
                              handleDeleteClick(group.menuPermissionGroup.id)
                            }
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Modal show={showModal} onHide={() => setShowModal(false)}>
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {editingGroup ? "Edit Group" : "Add Group"}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <Form onSubmit={handleFormSubmit}>
                      <Form.Group controlId="formGroupObjectId">
                        <Form.Label>Group ObjectID</Form.Label>
                        <Form.Control
                          type="text"
                          value={editingGroup?.groupObjectId || ""}
                          onChange={(e) =>
                            setEditingGroup((prev) =>
                              prev
                                ? { ...prev, groupObjectId: e.target.value }
                                : null
                            )
                          }
                          required
                        />
                      </Form.Group>
                      <Form.Group controlId="formGroupName">
                        <Form.Label>Group Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={editingGroup?.groupName || ""}
                          onChange={(e) =>
                            setEditingGroup((prev) =>
                              prev
                                ? { ...prev, groupName: e.target.value }
                                : null
                            )
                          }
                          required
                        />
                      </Form.Group>
                      <Form.Group controlId="formDescription">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={editingGroup?.description || ""}
                          onChange={(e) =>
                            setEditingGroup((prev) =>
                              prev
                                ? { ...prev, description: e.target.value }
                                : null
                            )
                          }
                          required
                        />
                      </Form.Group>
                      <Form.Group controlId="formStatus">
                        <Form.Label>Status</Form.Label>
                        <Form.Control
                          as="select"
                          value={editingGroup?.active ? "Active" : "Inactive"}
                          onChange={(e) =>
                            setEditingGroup((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    active: e.target.value === "Active",
                                  }
                                : null
                            )
                          }
                          required
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </Form.Control>
                      </Form.Group>
                      <Button className="m-2" variant="primary" type="submit">
                        {editingGroup ? "Update" : "Add"}
                      </Button>
                      <Button
                        className="m-2"
                        variant="secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Cancel
                      </Button>
                    </Form>
                  </Modal.Body>
                </Modal>
              </>
            )}
          </div>
        </div>
        <div className="col-12">
          <Footer />
        </div>
      </div>
    </>
  );
};

export default PermissionGroupMenu;
